import React, { useState, useEffect } from "react";
import { MDBInput } from "mdb-react-ui-kit";
import apiService from "../../../Services/apiServices";
import Search from "../../../PageComponents/Search/Search";
import helper from "../../../Services/helper";
import { formatData } from "../../exportFunctions/exportFunction";

function ShareHolderEquity() {
    const initStartDate = new Date(new Date().getFullYear(), 0, 1, 1);
    const initEndDate = new Date();
    const [searchParams, setSearchParams] = useState({ projectId: 0, branchId: 0, startDate: initStartDate, endDate: initEndDate });
    const [equityData, setEquityData] = useState(null);
    const [showProgress, setShowProgress] = useState(false);

    const calcBalance = (accounts) => accounts.reduce((sum, a) => sum + (a.credits - a.debits), 0);

    const getGeneralBookWithDetails = (startDate, endDate) => {
        let strStartDate = helper().formatDate(startDate, 'MM-dd-yyyy');
        let strEndDate = helper().formatDate(endDate, 'MM-dd-yyyy') + " 23:59:59";

        setShowProgress(true);

        var params = `&startDate=${strStartDate}&endDate=${strEndDate}&branchId=0&projectId=0`;
        apiService().query('fnGeneralBookWithDetails', params).then((response) => {
            if (response.success) {
                let rawData = response.data;

                // Net profit from income statement
                let incFormatted = formatData(rawData);
                let netProfit = parseFloat(incFormatted.at(-1).currentValue) || 0;

                // Equity accounts (class 3): credits - debits
                let equityAccounts = rawData.filter(i => i.classId.toString().startsWith('3'));
                let shareCapital = equityAccounts.filter(i => i.classId.toString().startsWith('30'));
                let retainedEarnings = equityAccounts.filter(i => i.classId.toString().startsWith('32'));
                let reserves = equityAccounts.filter(i => {
                    let cid = i.classId.toString();
                    return cid.startsWith('3') && !cid.startsWith('30') && !cid.startsWith('32');
                });

                setEquityData({
                    shareCapital: calcBalance(shareCapital),
                    retainedEarnings: calcBalance(retainedEarnings),
                    reserves: calcBalance(reserves),
                    netProfit: netProfit,
                });

                setSearchParams(prev => ({ ...prev, startDate, endDate }));
                setShowProgress(false);
            } else {
                console.log('Error:' + response.errorMessage);
                setShowProgress(false);
            }
        });

        // Dividends
        let divParams = `&startDate=${strStartDate}&endDate=${strEndDate}&branchId=0&projectId=0`;
        apiService().query('dividentat', divParams).then((response) => {
            if (response.success) {
                let divAmount = response.data[0]?.TotalDebits || 0;
                setEquityData(prev => prev ? { ...prev, dividends: divAmount } : prev);
            }
        });
    };

    useEffect(() => {
        getGeneralBookWithDetails(initStartDate, initEndDate);
    }, []);

    const onSearchClick = () => {
        if (searchParams.startDate && searchParams.endDate) {
            getGeneralBookWithDetails(searchParams.startDate, searchParams.endDate);
        }
    };

    const fmt = (val) => helper().formatNumber(val || 0);

    const Row = ({ label, shareCapital, retained, reserves, total, bold, border }) => {
        let cls = bold ? "font-weight-bold text-dark" : "";
        let inputCls = `form-control form-control-sm font-weight-bold text-right ${border ? "border-dark" : ""}`;
        return (
            <div className={`row ${bold ? "mt-3" : "mt-1"} inputMinWitdth`}>
                <div className="col-12 col-md-4 d-flex justify-content-center justify-content-md-start">
                    <div className="text-left">
                        <p className={`${cls} m-0`}>{bold ? <u>{label}</u> : label}</p>
                    </div>
                </div>
                <div className="col-3 col-md-2 d-flex justify-content-center justify-content-md-start">
                    <input type="text" className={inputCls} disabled value={fmt(shareCapital)} />
                </div>
                <div className="col-3 col-md-2 d-flex justify-content-center justify-content-md-start">
                    <input type="text" className={inputCls} disabled value={fmt(retained)} />
                </div>
                <div className="col-3 col-md-2 d-flex justify-content-center justify-content-md-start">
                    <input type="text" className={inputCls} disabled value={fmt(reserves)} />
                </div>
                <div className="col-3 col-md-2 d-flex justify-content-center justify-content-md-start">
                    <input type="text" className={inputCls} disabled value={fmt(total)} />
                </div>
            </div>
        );
    };

    const d = equityData || {};
    const sc = d.shareCapital || 0;
    const re = d.retainedEarnings || 0;
    const res = d.reserves || 0;
    const np = d.netProfit || 0;
    const div = d.dividends || 0;

    const openingTotal = sc + re + res;
    const closingRe = re + np - div;
    const closingTotal = sc + closingRe + res;

    const year = searchParams.endDate ? searchParams.endDate.getFullYear() : new Date().getFullYear();

    return (
        <div className="container-fluid px-2 mt-3">
            <Search back="#/reportsMap/2" />
            <div className="card shadow">
                <div className="card-header border-left-primary py-2">
                    <h6 className="m-0 font-weight-bold text-primary text-center">{helper().translate("SHAREHOLDER_EQUITY")}</h6>
                </div>
                {showProgress &&
                    <div style={{ position: 'absolute', top: '10px', right: '40px', zIndex: 1000, color: '#4E73DF', fontSize: 18 }}>{helper().translate('LOADING')} ...</div>
                }
            </div>
            <div className="card border-left-primary shadow h-100 mt-1 pt-1 pb-3 text-dark">
                <div className="container">
                    {/* Date period selection */}
                    <div className="row mt-3 mb-2">
                        <div className="col-12 col-md-8 offset-md-2">
                            <div className="row no-gutters align-items-center">
                                <div className="col-lg-4 mb-2 mb-lg-0 pe-1">
                                    <MDBInput
                                        name="startDate"
                                        className="form-control-sm active"
                                        onChange={(e) => setSearchParams({ ...searchParams, startDate: new Date(e.target.value) })}
                                        type="date"
                                        label="Nga"
                                        value={helper().isValidDate(searchParams.startDate) ? searchParams.startDate.toISOString().substring(0, 10) : ''}
                                    />
                                </div>
                                <div className="col-lg-4 mb-2 mb-lg-0 pe-1">
                                    <MDBInput
                                        name="endDate"
                                        className="form-control-sm active"
                                        onChange={(e) => setSearchParams({ ...searchParams, endDate: new Date(e.target.value) })}
                                        type="date"
                                        label="Deri"
                                        value={helper().isValidDate(searchParams.endDate) ? searchParams.endDate.toISOString().substring(0, 10) : ''}
                                    />
                                </div>
                                <div className="col-lg-2 mb-2 mb-lg-0 d-flex align-items-center">
                                    <a className="searchbutton" onClick={() => onSearchClick()}>
                                        <i className="fas fa-search fa-2x text-dark" />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row font-weight-bold my-4">
                        <div className="col-12 d-flex justify-content-center text-center">
                            <h6 className="m-0 w-100 font-weight-bold text-primary text-center">
                                Pasqyra e ndryshimeve ne ekuitet per vitin qe perfundon me: {helper().formatDate(searchParams.endDate)}
                            </h6>
                        </div>
                    </div>

                    {/* Column Headers */}
                    <div className="row mt-2 font-weight-bold inputMinWitdth">
                        <div className="col-12 col-md-4 d-flex justify-content-center justify-content-md-start"></div>
                        <div className="col-3 col-md-2 d-flex justify-content-center">
                            <label><p className="font-weight-bold text-dark m-0"><u>Kapitali aksionar</u></p></label>
                        </div>
                        <div className="col-3 col-md-2 d-flex justify-content-center">
                            <label><p className="font-weight-bold text-dark m-0"><u>Fitimet e mbajtura</u></p></label>
                        </div>
                        <div className="col-3 col-md-2 d-flex justify-content-center">
                            <label><p className="font-weight-bold text-dark m-0"><u>Rezervat e tjera</u></p></label>
                        </div>
                        <div className="col-3 col-md-2 d-flex justify-content-center">
                            <label><p className="font-weight-bold text-dark m-0"><u>Gjithsej</u></p></label>
                        </div>
                    </div>

                    {/* Opening Balance */}
                    <Row
                        label={`Gjendja me 1 janar ${year}`}
                        shareCapital={sc}
                        retained={re}
                        reserves={res}
                        total={openingTotal}
                        bold={true}
                        border={true}
                    />

                    {/* Changes */}
                    <Row
                        label="Fitimi / (humbja) i/e vitit"
                        shareCapital={0}
                        retained={np}
                        reserves={0}
                        total={np}
                    />

                    <Row
                        label="Dividendat"
                        shareCapital={0}
                        retained={div > 0 ? -div : 0}
                        reserves={0}
                        total={div > 0 ? -div : 0}
                    />

                    {/* Closing Balance */}
                    <div className="row mt-2">
                        <div className="col-12 col-md-11 text-dark">
                            <hr className="m-0" style={{ borderTop: '2px solid #333' }}></hr>
                        </div>
                    </div>

                    <Row
                        label={`Gjendja me 31 dhjetor ${year}`}
                        shareCapital={sc}
                        retained={closingRe}
                        reserves={res}
                        total={closingTotal}
                        bold={true}
                        border={true}
                    />

                </div>
            </div>
        </div>
    );
}

export default ShareHolderEquity;
